import { useCallback, useEffect, useState } from "react";
import { Sparkles, Check, X, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import api from "../../api";

// Drafts the session assistant made from the call transcript (backend
// clinical/notes/session_assist.py). Suggestions only: nothing goes into the
// note, the patient's homework, the follow-ups or the plan until the clinician
// presses Use, and nothing they use reaches the patient until the note is
// signed off (backend clinical/notes/session_release.py). One hook per
// consultation page; one <SessionDraftBox> per tab.

export const NOTE_LABELS = {
  summary: "Session summary",
  mood: "Mood & affect",
  mainthemes: "Main themes",
  resistanceorengagement: "Engagement & resistance",
  interventions: "Interventions used",
  interventionseffectiveness: "Effectiveness of interventions",
  updatedhypothesis: "Updated hypothesis",
  sessionoutcome: "Session outcome",
  nextplan: "Next session's plan",
};

export function useSessionAssist(appointmentId, active = true) {
  const [assist, setAssist] = useState(null);

  const load = useCallback(() => {
    if (!appointmentId || !active) return;
    api.getSessionAssist(appointmentId).then((r) => setAssist(r.data)).catch(() => setAssist(null));
  }, [appointmentId, active]);

  useEffect(() => { load(); }, [load]);

  // A redraft takes a minute or two after a retag; check back until it's in.
  useEffect(() => {
    if (!assist?.updating) return undefined;
    const t = setTimeout(load, 20000);
    return () => clearTimeout(t);
  }, [assist, load]);

  const markSection = useCallback((section, state) => {
    if (!assist?.run_id) return;
    api.setSessionAssistSection(appointmentId, { run_id: assist.run_id, section, state })
      .then((r) => setAssist(r.data)).catch(() => {});
  }, [appointmentId, assist]);

  const retry = useCallback(() => {
    api.retrySessionAssist(appointmentId).then((r) => setAssist(r.data)).catch(() => {});
  }, [appointmentId]);

  // Redo the drafts with what the clinician has saved in the note so far.
  const [redraftError, setRedraftError] = useState("");
  const redraft = useCallback(() => {
    setRedraftError("");
    api.redraftSessionAssist(appointmentId).then((r) => setAssist(r.data))
      .catch((e) => setRedraftError(e.response?.data?.error || "Couldn't redo the drafts. Please try again."));
  }, [appointmentId]);

  return { assist, setAssist, reload: load, markSection, retry, redraft, redraftError };
}

const addText = (current, text) => ((current || "").trim() ? `${current.trim()}\n\n${text}` : text);

export const addDays = (isoDate, days) => {
  const d = isoDate ? new Date(`${isoDate}T00:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

function Shell({ title, tone = "violet", children, onDismiss, used, onUndo, banner }) {
  const tones = {
    violet: "border-violet-200 bg-violet-50/40 text-violet-900",
    amber: "border-amber-200 bg-amber-50/60 text-amber-900",
  };
  if (used) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-secondary-200 bg-white px-3 py-2 text-xs text-secondary-600">
        <Check className="h-3.5 w-3.5 text-emerald-600" /> {title}: used.
        <button type="button" onClick={onUndo} className="ml-auto font-semibold text-violet-700 hover:underline">Show again</button>
      </div>
    );
  }
  return (
    <div className={`mb-4 rounded-xl border p-3 ${tones[tone]}`} role="region" aria-label={title}>
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0" />
        <p className="flex-1 text-sm font-bold">{title}</p>
        {onDismiss && (
          <button type="button" onClick={onDismiss} aria-label="Dismiss draft"
            className="rounded p-1 text-secondary-500 hover:bg-white hover:text-secondary-800">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {banner}
      {children}
    </div>
  );
}

const UseButton = ({ onClick, children = "Use", disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className="shrink-0 rounded-lg bg-violet-600 px-2.5 py-1 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50">
    {children}
  </button>
);

/**
 * section: "note" | "patient_summary" | "homework" | "follow_ups" | "plan" | "risk"
 * note:            values (consultation data), onApplyNote({field: text}) / onApplyTemplate({key: text}), templateLabels
 * patient_summary: currentSummary, onApplySummary(text)
 * homework:        patientId, session {consultationId, appointmentId}, onAssigned()
 * follow_ups:      onAddFollowUps([{kind, body, trigger_date}]), sessionDate
 */
export function SessionDraftBox({ hook, section, ...p }) {
  const { assist, markSection, retry, redraft, redraftError } = hook;
  const [picked, setPicked] = useState({});
  const [doneItems, setDoneItems] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  if (!assist || !assist.enabled || !assist.transcript) return null;

  const status = assist.status;
  // Only one place says what's happening when there are no drafts to show.
  if (!assist.drafts) {
    if (section !== "note") return null;
    if (assist.updating) {
      return <Shell title="Session drafts"><p className="flex items-center gap-2 text-xs"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the session transcript. Drafts usually appear within a few minutes.</p></Shell>;
    }
    if (status === "failed") {
      return (
        <Shell title="Session drafts" tone="amber">
          <p className="mb-2 text-xs">The drafts couldn't be made from the transcript. Your notes aren't affected.</p>
          <button type="button" onClick={retry} className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold"><RefreshCw className="h-3.5 w-3.5" /> Try again</button>
        </Shell>
      );
    }
    return null;
  }

  const d = assist.drafts;
  const state = (assist.section_state || {})[section];
  if (state === "dismissed") return null;
  const used = state === "used";
  const dismiss = () => markSection(section, "dismissed");
  const undo = () => markSection(section, null);
  const banner = assist.updating ? (
    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold"><Loader2 className="h-3 w-3 animate-spin" /> Redrafting with the speakers you corrected. This draft is from before.</p>
  ) : assist.status === "roles_only" && assist.message ? (
    <p className="mb-2 text-[11px] font-semibold">{assist.message}</p>
  ) : null;
  const doneItem = (key) => setDoneItems((x) => ({ ...x, [key]: true }));

  if (section === "note") {
    // Drafts follow the format the session had when they were made; if the
    // page has since switched format, they can't be dropped into it.
    const draftFormat = d.template_code || "default";
    const pageFormat = (p.values?.template_code) || "default";
    if (draftFormat !== pageFormat) {
      return (
        <Shell title="Draft note from the session" onDismiss={dismiss}>
          <p className="text-xs">These drafts were written in the {draftFormat === "default" ? "standard Clinical note" : (p.templateNames?.[draftFormat] || draftFormat)} format. Switch the note format back to use them.</p>
        </Shell>
      );
    }
    const isTemplate = Object.keys(d.template_values || {}).length > 0;
    const entries = isTemplate ? Object.entries(d.template_values) : Object.entries(d.note || {});
    if (!entries.length) return null;
    const labels = isTemplate ? (p.templateLabels || {}) : NOTE_LABELS;
    const current = isTemplate ? (p.values?.template_values || {}) : (p.values || {});
    const apply = (pairs) => {
      const out = {};
      pairs.forEach(([k, v]) => { out[k] = addText(current[k], v); });
      if (isTemplate) p.onApplyTemplate(out); else p.onApplyNote(out);
    };
    const useAllEmpty = () => {
      apply(entries.filter(([k]) => !(current[k] || "").trim()));
      markSection("note", "used");
    };
    return (
      <Shell title="Draft note from the session" onDismiss={dismiss} used={used} onUndo={undo} banner={banner}>
        <p className="mb-2 text-xs">Written from the transcript and the notes you'd saved when it was made. Check it against your memory of the session before you use it.</p>
        <div className="space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-start gap-2 rounded-lg bg-white p-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-secondary-600">{labels[k] || k}</p>
                <p className="whitespace-pre-line text-sm text-secondary-800">{v}</p>
              </div>
              {doneItems[k] ? <Check className="h-4 w-4 text-emerald-600" /> : (
                <UseButton onClick={() => { apply([[k, v]]); doneItem(k); }}>{(current[k] || "").trim() ? "Add" : "Use"}</UseButton>
              )}
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
          {assist.reruns_left > 0 && !assist.updating && (
            <button type="button" onClick={redraft} title="Save the consultation first so the drafts can use what you've written"
              className="inline-flex items-center gap-1 rounded-lg border border-violet-300 bg-white px-2.5 py-1 text-xs font-semibold text-violet-800 hover:bg-violet-50">
              <RefreshCw className="h-3.5 w-3.5" /> Redraft with my saved notes ({assist.reruns_left} left)
            </button>
          )}
          <UseButton onClick={useAllEmpty}>Fill the empty fields</UseButton>
        </div>
        {redraftError && <p className="mt-1 text-right text-xs text-red-700">{redraftError}</p>}
      </Shell>
    );
  }

  if (section === "patient_summary") {
    if (!d.patient_summary) return null;
    return (
      <Shell title="Draft summary for the patient" onDismiss={dismiss} used={used} onUndo={undo} banner={banner}>
        <p className="mb-2 whitespace-pre-line rounded-lg bg-white p-2 text-sm text-secondary-800">{d.patient_summary}</p>
        <p className="mb-2 text-[11px]">The patient sees it only after you sign off the note.</p>
        <div className="flex justify-end">
          <UseButton onClick={() => { p.onApplySummary(addText(p.currentSummary, d.patient_summary)); markSection(section, "used"); }}>
            {(p.currentSummary || "").trim() ? "Add to the summary" : "Use"}
          </UseButton>
        </div>
      </Shell>
    );
  }

  if (section === "homework") {
    if (!(d.homework || []).length) return null;
    const assign = (h, i) => {
      setBusy(i); setErr("");
      api.assignHomework(p.patientId, {
        item_code: h.item_code, note: h.details || undefined,
        consultation_id: p.session?.consultationId || undefined, appointment_id: p.session?.appointmentId || undefined,
      }).then(() => { doneItem(i); p.onAssigned && p.onAssigned(); })
        .catch((e) => setErr(e.response?.data?.error || "Couldn't assign it. Please try again."))
        .finally(() => setBusy(null));
    };
    return (
      <Shell title="Homework agreed in the session" onDismiss={dismiss} used={used} onUndo={undo} banner={banner}>
        <div className="space-y-2">
          {d.homework.map((h, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-white p-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-secondary-900">{h.title || "Homework"}</p>
                {h.details && <p className="text-xs text-secondary-700">{h.details}</p>}
                {!h.item_code && <p className="mt-0.5 text-[11px] text-secondary-500">No matching toolkit item. Assign one below, or add it to the next session's plan.</p>}
              </div>
              {doneItems[i] ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700"><Check className="h-4 w-4" /> Assigned</span>
                : h.item_code && <UseButton disabled={busy === i} onClick={() => assign(h, i)}>{busy === i ? "Assigning..." : "Assign"}</UseButton>}
            </div>
          ))}
        </div>
        {err && <p className="mt-2 text-xs text-red-700">{err}</p>}
        <p className="mt-2 text-[11px]">Assigned homework reaches the patient's app once you sign off the note.</p>
      </Shell>
    );
  }

  if (section === "follow_ups") {
    if (!(d.follow_ups || []).length) return null;
    const toItem = (f) => ({ kind: f.kind, body: f.body, trigger_date: addDays(p.sessionDate, f.days_from_now || 7) });
    return (
      <Shell title="Follow-ups mentioned in the session" onDismiss={dismiss} used={used} onUndo={undo} banner={banner}>
        <div className="space-y-2">
          {d.follow_ups.map((f, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-white p-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-secondary-600">
                  {f.kind === "patient_msg" ? "Message to the patient" : "Task for you"} · in {f.days_from_now} day{f.days_from_now === 1 ? "" : "s"}
                </p>
                <p className="text-sm text-secondary-800">{f.body}</p>
              </div>
              {doneItems[i] ? <Check className="h-4 w-4 text-emerald-600" />
                : <UseButton onClick={() => { p.onAddFollowUps([toItem(f)]); doneItem(i); }}>Add</UseButton>}
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px]">Added items appear in the list below; save the consultation to keep them.</p>
      </Shell>
    );
  }

  if (section === "plan") {
    if (!(d.plan || []).length) return null;
    const label = { new_goal: "New goal", update_goal: "Change a goal", achieved: "Goal reached" };
    const chosen = d.plan.filter((_, i) => picked[i]);
    const putInPlan = () => {
      p.onApplyPlan && p.onApplyPlan(chosen);
      setPicked({});
      markSection(section, "used");
    };
    return (
      <Shell title="Treatment plan suggestions" onDismiss={dismiss} used={used} onUndo={undo} banner={banner}>
        <div className="space-y-2">
          {d.plan.map((s, i) => (
            <label key={i} className="normal-case font-sans tracking-normal flex cursor-pointer items-start gap-2 rounded-lg bg-white p-2">
              <input type="checkbox" className="mt-1 h-4 w-4" checked={!!picked[i]} onChange={(e) => setPicked((x) => ({ ...x, [i]: e.target.checked }))} />
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-bold text-violet-700">{label[s.type]}</span>
                {s.goal && <span className="block text-sm text-secondary-900">{s.goal}</span>}
                {s.reason && <span className="block text-xs text-secondary-600">{s.reason}</span>}
              </span>
            </label>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[11px]">Opens the plan below with the ones you tick, for you to edit and save. It's shared only if you tick "Share with the patient", and the patient sees it after you sign off the note.</p>
          <UseButton disabled={!chosen.length} onClick={putInPlan}>Put in the plan</UseButton>
        </div>
      </Shell>
    );
  }

  if (section === "risk") {
    if (!(d.risk || []).length) return null;
    return (
      <Shell title="Worth a second look" tone="amber" onDismiss={dismiss} banner={banner}>
        <ul className="space-y-1.5">
          {d.risk.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span><span className="font-semibold">"{r.quote}"</span>{r.why ? <span className="text-xs"> · {r.why}</span> : null}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[11px]">Pointed out from the transcript, not assessed. Your risk assessment below is what counts.</p>
      </Shell>
    );
  }
  return null;
}
